import { ICreateDeviceCheckLookupsResponse } from '@ltvco/refresh-lib/api';
import { useCreateDeviceCheckLookups } from '@ltvco/refresh-lib/hooks';
import { Box, Grid, Stack, styled } from '@ltvco/refresh-lib/theme';
import {
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
} from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  DeviceCheckLoading,
  DeviceCard,
  DeviceCheckDetails,
  DeviceHistoryTable,
  DeviceInfoCard,
  GSMADeviceStatus,
} from 'components/DeviceCheck';
import { Sidenav } from 'components/sidenav/Sidenav';
import { getFraudDeviceNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';

const ReportBody = styled('div')(({ theme }) => ({
  width: '100%',
  margin: 'auto',

  [theme.breakpoints.up('lg')]: {
    margin: 'auto',
    maxWidth: '1100px',
  },
}));

export function DeviceFraudReportPage() {
  const [hasLookupError, setHasLookupError] = useState(false);
  const { search } = useLocation();
  const deviceId = new URLSearchParams(search).get('permalink');
  const [deviceCheckLookups, setDeviceCheckLookups] =
    useState<ICreateDeviceCheckLookupsResponse | null>(null);
  const { createDeviceCheckLookups, isLoading } = useCreateDeviceCheckLookups({
    onSuccess: (data) => {
      setHasLookupError(false);
      setDeviceCheckLookups(data);
    },
    onError: (error) => {
      setDeviceCheckLookups(null);
      setHasLookupError(true);
      console.log('ERROR:', error);
    },
  });

  useEffect(() => {
    if (!deviceId) {
      return;
    }
    setHasLookupError(false);
    setDeviceCheckLookups(null);

    createDeviceCheckLookups({
      device_id: deviceId,
    });
  }, [deviceId, createDeviceCheckLookups]);

  if (!deviceId) {
    return null;
  }

  if (isLoading) {
    return (
      <Grid sx={{ bgcolor: 'background.default' }} container>
        <Sidenav onReport={true} />
        <ReportBody>
          <DeviceCheckLoading deviceId={deviceId} isLoading={isLoading} />
        </ReportBody>
      </Grid>
    );
  }

  if (hasLookupError || !deviceCheckLookups) {
    return <Box>Error fetching device check lookups</Box>;
  }

  const cleanedImeiHistory = deviceCheckLookups?.imeihistory?.filter((item) => {
    return Object.values(item).some((value) => value !== 'NA');
  });

  document.title = `${deviceId} - BeenVerified`;

  const fraudDeviceNavLinkDataV2 = getFraudDeviceNavLinkDataV2({
    historyLength: cleanedImeiHistory?.length ?? 0,
  });

  return (
    <Grid sx={{ bgcolor: 'background.default' }} container>
      <Sidenav onReport={true} />
      <ReportBody>
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box
              sx={{
                display: { xs: 'none', lg: 'block' },
                flexShrink: 0,
              }}
            >
              <ReportNavigationMenu
                reportType="Fraud Scan"
                headerTitle={deviceId}
                menuItems={
                  fraudDeviceNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
                }
              />
            </Box>
            <Box
              marginTop={0}
              sx={{
                width: '100%',
                maxWidth: '800px',
                minWidth: 0,
              }}
            >
              <Stack sx={{ mt: 2 }}>
                <Box id="device-fraud-overview-section" sx={{ width: '100%' }}>
                  <GSMADeviceStatus
                    blocklistStatus={deviceCheckLookups?.blockliststatus}
                    refCode={deviceCheckLookups?.refcode?.toString()}
                  />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    mt={2}
                    sx={{ width: '100%', maxWidth: 900 }}
                  >
                    <DeviceInfoCard
                      title="Block List"
                      status={deviceCheckLookups?.blockliststatus}
                      subWhenYes="Flagged"
                      subWhenNo="Not flagged"
                    />
                    <DeviceInfoCard
                      title="General List"
                      status={deviceCheckLookups?.generalliststatus}
                      subWhenYes="Has claims"
                      subWhenNo="No claims"
                    />
                    <DeviceInfoCard
                      title="Status Events"
                      value={String(cleanedImeiHistory?.length ?? 0)}
                      sub={
                        cleanedImeiHistory?.length
                          ? `${cleanedImeiHistory.length} events`
                          : 'No history'
                      }
                    />
                    <DeviceInfoCard
                      title="Device Type"
                      value={deviceCheckLookups?.devicetype || 'N/A'}
                      sub={deviceCheckLookups?.operatingsys || 'N/A'}
                    />
                  </Stack>
                </Box>

                <DeviceCard
                  manufacturer={deviceCheckLookups?.manufacturer || ''}
                  marketingname={deviceCheckLookups?.marketingname || ''}
                  modelname={deviceCheckLookups?.modelname || ''}
                  devicetype={deviceCheckLookups?.devicetype || ''}
                  operatingsys={deviceCheckLookups?.operatingsys || ''}
                  nfc={deviceCheckLookups?.nfc || 'no'}
                  bluetooth={deviceCheckLookups?.bluetooth || 'no'}
                  wlan={deviceCheckLookups?.WLAN || 'no'}
                />

                <DeviceCheckDetails
                  deviceid={deviceCheckLookups?.deviceid}
                  refcode={deviceCheckLookups?.refcode?.toString()}
                  manufacturer={deviceCheckLookups?.manufacturer}
                  marketingname={deviceCheckLookups?.marketingname}
                  modelname={deviceCheckLookups?.modelname}
                  devicetype={deviceCheckLookups?.devicetype}
                  operatingsys={deviceCheckLookups?.operatingsys}
                  nfc={deviceCheckLookups?.nfc || 'no'}
                  bluetooth={deviceCheckLookups?.bluetooth || 'no'}
                  wlan={deviceCheckLookups?.WLAN || 'no'}
                  band={deviceCheckLookups?.band || ''}
                />

                <DeviceHistoryTable imeihistory={cleanedImeiHistory} />
              </Stack>
            </Box>
          </Stack>
          <ReportNavigationMobileMenu
            reportType="Fraud Scan"
            headerTitle={deviceId}
            menuItems={
              fraudDeviceNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
      </ReportBody>
    </Grid>
  );
}
